import React, { FormEvent, useContext, useEffect, useState } from 'react'
import ProgressBar from '_COMPONENTS/GENERAL/ProgressBar/ProgressBar';
import { translate } from 'translation';
import { classNames, useCallApi } from 'utils/helpers';
import { useNotification } from '_CONTEXT/hook/contextHook';
import { PopupContext } from '_CONTEXT/PopupContext';
import { appDataStore } from '_STORES/AppData';
import { hookStore } from '_STORES/Hooks';
import { Hook, HookField } from 'Types';
import Tooltip from '_COMPONENTS/GENERAL/ToolType/ToolType.';
import RenderIf from '_COMPONENTS/GENERAL/RenderIf';
import SyncPreviewModal from './SyncPreviewModal';

interface SyncData {
    running: boolean;
    sync_progress_users: number;
    sync_progress_orders: number;
    last_heartbeat: string;
    last_error: string;
    last_sinced_date: Date | null;
    sync_additional_datas: {
        total_users: number;
        current_user: number;
        total_orders: number;
        current_order: number;
        current_user_index: number;
        current_order_index: number;
        total_person_errors: number;
        total_person_uptodate: number;
        total_person_done: number;
        total_order_errors: number;
        total_order_uptodate: number;
        total_order_done: number;
    }
}

export const emptySyncData = {
    running: false,
    sync_progress_users: 0,
    sync_progress_orders: 0,
    last_heartbeat: "",
    last_error: "",
    sync_additional_datas: {
        total_users: 0,
        current_user: 0,
        total_orders: 0,
        current_order: 0,
        current_user_index: 0,
        current_order_index: 0,
        total_person_errors: 0,
        total_person_uptodate: 0,
        total_person_done: 0,
        total_order_errors: 0,
        total_order_uptodate: 0,
        total_order_done: 0,
    },
    last_sinced_date: null,
}

interface Props {
    checkPipedriveApi: (e: FormEvent, retry: boolean) => Promise<boolean>;
    checkW2pAPI: (e: FormEvent, retry: boolean) => Promise<boolean>;
}


export default function Syncronize({ checkPipedriveApi, checkW2pAPI }: Props) {

    const callApi = useCallApi()
    const { addNotification } = useNotification()
    const { addPopupContent, showPopup } = useContext(PopupContext)

    const [syncData, setSyncData] = useState<SyncData>(emptySyncData)

    useEffect(() => {
        const fetchSyncProgress = async () => {
            try {
                const res = await callApi(
                    `${appDataStore.appData.w2pcifw_client_rest_url}/sync-progress`,
                    { method: "GET" },
                    null,
                    { time: new Date().getTime() }
                );
                if (res?.data) {
                    setSyncData(prv => ({
                        ...prv,
                        ...res.data,
                        last_sinced_date: res.data?.last_sinced_date ? new Date(res.data?.last_sinced_date) : null,
                    }));
                }
            } catch (error) {
                console.error("Error fetching sync progress:", error);
            }
        };

        fetchSyncProgress();

        let intervalId: NodeJS.Timeout;

        intervalId = setInterval(() => {
            fetchSyncProgress();
        }, syncData.running ? 5000 : 20 * 1000);

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [syncData.running]);

    const stopSync = async (e: FormEvent) => {
        e.preventDefault()
        const response = await callApi(`${appDataStore.appData.w2pcifw_client_rest_url}/stop-sync`, { method: "get" }, null, { time: new Date().getTime() }, e)
        if (response?.status === 200) {
            addNotification({ error: false, content: translate("Synchronization will be stopped soon (it can take a few minutes)") })
        } else {
            addNotification({ error: true, content: translate("An error occured while stopping the synchronization") })
        }
    }


    // Collect synchronization data
    const collectSyncData = () => {
        const syncInfo: {
            personHook?: { hook: Hook; fields: HookField[] };
            organizationHook?: { hook: Hook; fields: HookField[] };
            dealHooks: { hook: Hook; fields: HookField[] }[];
        } = {
            dealHooks: []
        }

        // Find User Updated hook for persons
        const userUpdatedPreHook = appDataStore.appData.CONSTANTES.W2PCIFW_HOOK_LIST.find(
            hook => hook.source === "user" && hook.label.toLowerCase().includes("user updated")
        )

        if (userUpdatedPreHook) {
            // Person hook
            const personHook = hookStore.getHookFromPreHook(userUpdatedPreHook, "person")
            if (personHook.enabled) {
                const personFields = hookStore.getFields(personHook.id).filter(f => f.enabled)
                if (personFields.length > 0) {
                    syncInfo.personHook = { hook: personHook, fields: personFields }
                }
            }

            // Organization hook
            const organizationHook = hookStore.getHookFromPreHook(userUpdatedPreHook, "organization")
            if (organizationHook.enabled) {
                const organizationFields = hookStore.getFields(organizationHook.id).filter(f => f.enabled)
                if (organizationFields.length > 0) {
                    syncInfo.organizationHook = { hook: organizationHook, fields: organizationFields }
                }
            }
        }

        // Find all enabled deal hooks (only specific order hooks)
        const allowedOrderHooks = [
            "Order on hold",
            "Order processing",
            "Order pending",
            "Order completed",
            "Order refunded",
            "Order canceled",
            "Order failed"
        ]

        const dealPreHooks = appDataStore.appData.CONSTANTES.W2PCIFW_HOOK_LIST.filter(
            hook => !hook.disabledFor?.includes("deal") && allowedOrderHooks.includes(hook.label)
        )

        dealPreHooks.forEach(preHook => {
            const dealHook = hookStore.getHookFromPreHook(preHook, "deal")
            if (dealHook.enabled) {
                const dealFields = hookStore.getFields(dealHook.id).filter(f => f.enabled)
                if (dealFields.length > 0) {
                    syncInfo.dealHooks.push({ hook: dealHook, fields: dealFields })
                }
            }
        })

        return syncInfo
    }


    // Start synchronization (extracted from syncroniseAll)
    const startSync = async (resume: boolean) => {
        const w2pOk = await checkW2pAPI({ preventDefault: () => {} } as FormEvent, false)
        const pipeOk = await checkPipedriveApi({ preventDefault: () => {} } as FormEvent, false)

        if (w2pOk && pipeOk) {
            !resume && setSyncData(prv => ({
                ...prv,
                running: false,
                sync_progress_users: 0,
                sync_progress_orders: 0,
                last_heartbeat: "",
                last_error: "",
                sync_additional_datas: {
                    total_users: 0,
                    current_user: 0,
                    total_orders: 0,
                    current_order: 0,
                    current_user_index: 0,
                    current_order_index: 0,
                    total_person_errors: 0,
                    total_person_uptodate: 0,
                    total_person_done: 0,
                    total_order_errors: 0,
                    total_order_uptodate: 0,
                    total_order_done: 0,
                },
            }))
            callApi(
                `${appDataStore.appData.w2pcifw_client_rest_url}/start-sync`,
                { method: "GET" },
                null,
                { "resume": resume, time: new Date().getTime() }
            )
                .then(async res => {
                    setSyncData(prv => ({ ...prv, running: true }))
                    addNotification({
                        "error": !res?.data?.success,
                        "content": res?.data?.message
                            ? res.data.message
                            : res?.data?.success ? "Synchronization has started" : "Failed to start synchronization"
                    })
                })
                .catch(error => {
                    addNotification({
                        "error": true,
                        "content": translate("Wa have encountered an error, try again later")
                    })
                })
        }
    }

    const syncroniseAll = async (e: FormEvent, resume: boolean) => {
        e.preventDefault()
        
        // Collect synchronization data
        const syncInfo = collectSyncData()
        
        // Show preview modal
        const modalContent = (
            <SyncPreviewModal
                syncInfo={syncInfo}
                onCancel={() => {
                    showPopup(false)
                }}
                onStart={async () => {
                    showPopup(false)
                    await startSync(resume)
                }}
            />
        )
        addPopupContent(modalContent, "900px")
    }

    return (
        <div>
            <h2>Synchronize all existing data with Pipedrive</h2>
            <p className='m-b-10'>
                {translate(`This action will synchronize all orders and users from your site with Pipedrive. To prevent system overload, the synchronization process may take several hours or days depending on the number of items being sent to Pipedrive and your daily Pipedrive API limit.`)}
            </p>
            {syncData.running
                ? <div className='mt-4'>
                    <p>{translate("If the synchronization appears to be stuck for several minutes, you can try resuming it")}</p>
                    <div className='flex gap-2'>
                        <form onSubmit={e => syncroniseAll(e, true)}>
                            <button className='mt-2'>
                                {translate("Resume Synchronization")}
                            </button>
                        </form>
                        <div className='flex gap-1'>
                            <form onSubmit={e => stopSync(e)}>
                                <button
                                    className={classNames(
                                        "mt-2"
                                    )}
                                >
                                    {translate("Pause synchronization")}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
                : <>
                    <p className="mb-2">
                        {translate(`Before starting the synchronization, make sure you have properly configured the settings for the order and person hooks:`)}
                    </p>
                    <ul className="space-y-1 mb-6 text-muted-foreground text-sm list-disc list-inside">
                        <li className='pl-2'>
                            {translate(`During a full sync, the "User Updated" event is used to send user data to Pipedrive for persons and organizations (if defined). This hook is the most relevant for syncing general user information, as it is triggered anytime a user profile is updated — for example, after a new order or a profile change.`)}
                        </li>
                        <li className='pl-2'>
                            {translate(`For orders, the event corresponding to each order's current status (e.g., "Completed", "Processing") is used. Make sure you have configured those events under the "Deal" section if you want orders to be included in the sync.`)}
                        </li>
                    </ul>

                    {
                        syncData.last_error
                            ? <p className='text-red-700'>{translate("Error during sync: ")}{syncData.last_error}</p>
                            : null
                    }
                    <div className='flex gap-1'>
                        <form onSubmit={e => syncroniseAll(e, false)}>
                            <button
                                className={classNames(
                                    syncData.running ? "opacity-65 cursor-wait" : "",
                                    "mt-2"
                                )}
                            >
                                {syncData.sync_progress_users > 0
                                    ? translate("Restart Sync from Scratch")
                                    : translate("Start Full Synchronization")}
                            </button>
                        </form>
                        <RenderIf condition={syncData.sync_progress_users > 0 && syncData.sync_progress_orders < 100}>
                            <form onSubmit={e => syncroniseAll(e, true)}>
                                <button
                                    className={classNames(
                                        syncData.running ? "opacity-65 cursor-wait" : "",
                                        "mt-2"
                                    )}
                                >
                                    {translate("Resume synchronization")}
                                </button>
                            </form>
                        </RenderIf>
                    </div>
                </>
            }

            {
                syncData.running || syncData.last_heartbeat
                    ? <div className='mt-4'>
                        <div >
                            <div className='mb-2'>
                                <Tooltip
                                    tooltipText={
                                        <div className='text-center'>
                                            {`Errors during sync: ${syncData.sync_additional_datas.total_person_errors}`}
                                        </div>
                                    }
                                    mainText={<div>Users and organizations synchronization progress {`${syncData.sync_additional_datas.current_user_index} / ${syncData.sync_additional_datas.total_users}`}</div>
                                    }
                                />
                            </div>
                            <ProgressBar completed={syncData.sync_progress_users} running={syncData.running} />
                        </div>
                        <div className='mt-4'>
                            <div className='mb-2'>
                                <Tooltip
                                    tooltipText={
                                        <div className='text-center'>
                                            {`Errors during sync: ${syncData.sync_additional_datas.total_order_errors}`}
                                        </div>
                                    }
                                    mainText={<div>Orders synchronization progress {`${syncData.sync_additional_datas.current_order_index} / ${syncData.sync_additional_datas.total_orders}`}</div>
                                    }
                                />
                            </div>
                            <ProgressBar completed={syncData.sync_progress_orders} running={syncData.running} />
                        </div>
                    </div>
                    : null
            }

            {
                syncData.last_sinced_date
                    ? <div className='mt-4'>
                        <span className='font-semibold'>Last syncronisation : </span>
                        {syncData.last_sinced_date.toLocaleString()}
                    </div>
                    : null
            }</div>
    )
}
